import { Box, CircularProgress } from "@mui/material";
import { CSSProperties, useEffect, useRef, useState } from "react";

interface LazyLottieProps {
  /** Dynamic import function for the animation JSON, e.g. () => import("src/assets/Lotties/Truck.json") */
  importFn: () => Promise<{ default: unknown }>;
  style?: CSSProperties;
  loop?: boolean;
  autoplay?: boolean;
  onComplete?: () => void;
}

/**
 * Lazy-loads both the lottie-react library and animation JSON data on demand.
 * This prevents large animation files from being included in the initial bundle.
 */
export default function LazyLottie({
  importFn,
  style,
  loop = true,
  autoplay = true,
  onComplete,
}: LazyLottieProps) {
  const [LottieComponent, setLottieComponent] = useState<React.ComponentType<{
    animationData: unknown;
    style?: CSSProperties;
    loop?: boolean;
    autoplay?: boolean;
    onComplete?: () => void;
  }> | null>(null);
  const [animationData, setAnimationData] = useState<unknown>(null);
  const [failed, setFailed] = useState(false);
  const importFnRef = useRef(importFn);
  importFnRef.current = importFn;

  useEffect(() => {
    let cancelled = false;

    Promise.all([import("lottie-react"), importFnRef.current()])
      .then(([lottieModule, jsonModule]) => {
        if (!cancelled) {
          setLottieComponent(() => lottieModule.default);
          setAnimationData(jsonModule.default);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (failed) {
    return null;
  }

  if (!LottieComponent || !animationData) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={style}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <LottieComponent
      animationData={animationData}
      style={style}
      loop={loop}
      autoplay={autoplay}
      onComplete={onComplete}
    />
  );
}
